import { Box } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CaseNotes as CaseNotesTemplate } from '../../../../../packages/ui/templates/sign-off/components/case-notes';
import { getMemberNotes } from '../../../../../services/member/member.service';
import {
  getRecommendationNotes,
  deleteRecommendationNotes,
} from '../../../../../services/report-service/reportService.service';
import AddNoteDrawer from '../../../member-detail/components/Notes/AddNoteDrawer/AddNoteDrawer';
import NoteDrawer from '../../../member-detail/components/Notes/NoteDrawer';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import { commonActionCreators } from '../../../../../redux/modules/common';

export const DetectedCaseNotesSection = ({ appointment, handleOpenReasoning }) => {
  const dispatch = useDispatch();

  const [notes, setNotes] = useState([]);
  const [notesRecommendations, setNotesRecommendations] = useState([]);
  const [openAddNote, setOpenAddNote] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const fetchNotes = async () => {
    try {
      const { data } = await getMemberNotes({ userId: appointment?.patientId });
      setNotes(data.patientContactNotes);
    } catch (error) {
      console.error(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: error?.data?.errors[0]?.endUserMessage || 'Could not fetch notes.',
        }),
      );
    }
  };

  const fetchNotesRecommendations = async () => {
    try {
      const { data } = await getRecommendationNotes({ appointmentId: appointment?.appointmentId });
      setNotesRecommendations(
        data.message.recommendations.map(note => ({
          ...note,
          name: note.title,
          flag: note.type,
        })),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: error?.data?.errors[0]?.endUserMessage || 'Could not fetch notes recommendations.',
        }),
      );
    }
  };

  useEffect(() => {
    if (appointment?.patientId) {
      void fetchNotes();
      void fetchNotesRecommendations();
    }
  }, [appointment?.patientId, appointment?.appointmentId]);

  const handleAddNoteClick = () => {
    setOpenAddNote(true);
  };

  const handleCloseNote = () => {
    setOpenAddNote(false);
    setSelectedNote(null);
  };

  const handleUpdateNote = note => {
    setSelectedNote(note);
  };

  const handleEditNote = note => {
    setSelectedNote(note);
    setOpenAddNote(true);
  };

  const handleOpenChat = () => {
    dispatch(commonActionCreators.openDrawer('chat'));
  };

  const handleConfirmClick = note => {
    setSelectedNote(note);
    setOpenAddNote(true);
  };

  const handleDiscardRecommendation = useCallback(
    async recommendationId => {
      if (!recommendationId) {
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: 'Missing required information to discard recommendation.',
          }),
        );
        return;
      }

      try {
        await deleteRecommendationNotes({
          appointmentId: appointment.appointmentId,
          recommendationId: recommendationId,
        });

        fetchNotesRecommendations();
        dispatch(
          showSnackbar({
            snackType: 'success',
            snackMessage: 'Recommendation discarded successfully.',
          }),
        );
      } catch (error) {
        console.error('Error discarding recommendation:', error);
        dispatch(
          showSnackbar({
            snackType: 'error',
            snackMessage: error?.data?.errors?.[0]?.endUserMessage || 'Failed to discard recommendation.',
          }),
        );
      }
    },
    [appointment?.appointmentId, fetchNotesRecommendations],
  );
  return (
    <Box display="flex" gap={4} flexWrap="wrap">
      <CaseNotesTemplate
        caseNotes={notes}
        caseNotesRecommendations={notesRecommendations}
        onAddNoteClick={handleAddNoteClick}
        onChatClick={handleOpenChat}
        onDiscardRecommendation={handleDiscardRecommendation}
        appointmentId={appointment?.appointmentId}
        fetchNotesRecommendations={fetchNotesRecommendations}
        onNoteClick={handleUpdateNote}
        onRecommendationClick={handleConfirmClick}
        onReasoningClick={handleOpenReasoning}
      />

      <AddNoteDrawer
        isOpen={openAddNote}
        onClose={handleCloseNote}
        fetchNotes={fetchNotes}
        note={selectedNote}
        memberId={appointment?.patientId}
        appointmentId={appointment?.appointmentId}
        recommendationId={selectedNote?._id}
        fetchNotesRecommendations={fetchNotesRecommendations}
      />
      <NoteDrawer
        selectedNote={openAddNote ? undefined : selectedNote}
        onClose={() => setSelectedNote(null)}
        onUpdateNote={handleEditNote}
      />
    </Box>
  );
};
